/**
 * tdesign v1.1.1
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _slicedToArray } from '../_chunks/dep-605e961c.js';
import { _ as _defineProperty } from '../_chunks/dep-8c8a3203.js';
import React, { useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import forwardRefWithStatics from '../_util/forwardRefWithStatics.js';
import TabNav from './TabNav.js';
import { useTabClass } from './useTabClass.js';
import TabPanel from './TabPanel.js';
import { tabsDefaultProps } from './defaultProps.js';
import useDragSorter from '../_util/useDragSorter.js';
import '../_chunks/dep-f66447d3.js';
import 'hoist-non-react-statics';
import 'tdesign-icons-react';
import '../_chunks/dep-ac257584.js';
import '../_chunks/dep-8670d51c.js';
import '../_chunks/dep-73c3cd54.js';
import '../_chunks/dep-2055661d.js';
import '../_chunks/dep-c7d784af.js';
import '../_chunks/dep-37e82645.js';
import '../_chunks/dep-a0293295.js';
import '../_chunks/dep-dae4a585.js';
import '../_util/noop.js';
import './TabNavItem.js';
import '../_util/useRipple.js';
import '../hooks/useConfig.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-1d5f47c2.js';
import '../_chunks/dep-df9f8f04.js';
import '../_chunks/dep-9d326786.js';
import '../_chunks/dep-3542b5f1.js';
import '../_chunks/dep-79aca757.js';
import '../_chunks/dep-ced835e2.js';
import '../_chunks/dep-1e76a561.js';
import '../_chunks/dep-6b33e1e9.js';
import '../_chunks/dep-9e05eaf2.js';
import '../_chunks/dep-0d617aaa.js';
import '../_chunks/dep-030d278c.js';
import '../_chunks/dep-868937ef.js';
import '../_chunks/dep-6f5c07a7.js';
import '../_chunks/dep-a79a1775.js';
import '../_chunks/dep-7b744c82.js';
import '../_chunks/dep-0b20126b.js';
import '../_chunks/dep-2b7035c1.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-4fce6f2f.js';
import '../_common/js/global-config/default-config.js';
import '../_util/useAnimation.js';
import '../_util/setStyle.js';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-378695bd.js';
import '../_util/easing.js';
import '../hooks/useDomRefCallback.js';
import '../hooks/useGlobalIcon.js';
import './TabBar.js';
import '../_util/useMutationObserver.js';
import '../_common/js/tabs/base.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var Tabs = forwardRefWithStatics(function (props, ref) {
  var children = props.children,
    list = props.list,
    placement = props.placement,
    onRemove = props.onRemove,
    tabValue = props.value,
    onChange = props.onChange,
    className = props.className,
    style = props.style;
  var defaultValue = props.defaultValue;
  var _useTabClass = useTabClass(),
    tdTabsClassPrefix = _useTabClass.tdTabsClassPrefix,
    tdTabsClassGenerator = _useTabClass.tdTabsClassGenerator,
    tdClassGenerator = _useTabClass.tdClassGenerator;
  var targetClassNameRegExpStr = "^".concat(tdTabsClassPrefix, "(__nav-item|__nav-item-wrapper|__nav-item-text-wrapper)");
  var _useDragSorter = useDragSorter(_objectSpread(_objectSpread({}, props), {}, {
      sortOnDraggable: props.dragSort,
      onDragOverCheck: {
        x: true,
        targetClassNameRegExp: new RegExp(targetClassNameRegExpStr)
      }
    })),
    getDragProps = _useDragSorter.getDragProps;
  var memoChildren = useMemo(function () {
    if (!list || list.length === 0) {
      return children;
    }
    return list.map(function (panelProps) {
      return /* @__PURE__ */React.createElement(TabPanel, _objectSpread({
        key: panelProps.value
      }, panelProps));
    });
  }, [children, list]);
  var itemList = React.Children.map(memoChildren, function (child) {
    if (child && child.type === TabPanel) {
      return child.props;
    }
    return null;
  });
  if (defaultValue === void 0 && Array.isArray(itemList) && itemList.length !== 0) {
    defaultValue = itemList[0].value;
  }
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  useEffect(function () {
    tabValue !== void 0 && setValue(tabValue);
  }, [tabValue]);
  var handleChange = function handleChange(v) {
    if (tabValue === void 0) {
      setValue(v);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(v);
  };
  var handleClickTab = function handleClickTab(v) {
    if (tabValue === void 0) {
      setValue(v);
    }
  };
  var renderHeader = function renderHeader() {
    return /* @__PURE__ */React.createElement("div", {
      className: classNames(tdTabsClassGenerator("header"), tdClassGenerator("is-".concat(placement)))
    }, /* @__PURE__ */React.createElement(TabNav, _objectSpread(_objectSpread({}, props), {}, {
      getDragProps: getDragProps,
      activeValue: value,
      onRemove: onRemove,
      itemList: itemList,
      tabClick: handleClickTab,
      onChange: handleChange
    })));
  };
  return /* @__PURE__ */React.createElement("div", {
    ref: ref,
    className: classNames(tdTabsClassPrefix, className),
    style: style
  }, placement !== "bottom" ? renderHeader() : null, /* @__PURE__ */React.createElement("div", {
    className: classNames(tdTabsClassGenerator("content"), tdClassGenerator("is-".concat(placement)))
  }, React.Children.map(memoChildren, function (child) {
    if (child && child.type === TabPanel) {
      if (child.props.value === value) {
        return child;
      }
      if (child.props.destroyOnHide === false) {
        return /* @__PURE__ */React.createElement(TabPanel, {
          style: {
            display: "none"
          }
        }, child.props.children);
      }
    }
    return null;
  })), placement === "bottom" ? renderHeader() : null);
}, {
  TabPanel: TabPanel
});
Tabs.displayName = "Tabs";
Tabs.defaultProps = tabsDefaultProps;

export { Tabs as default };
//# sourceMappingURL=Tabs.js.map
